import { coreTypes } from "@core/core-types.di";
import type { Http } from "@core/data/infrastructures/http/http";
import { FallbackError } from "@core/domain/errors/fallback.error";
import { Either } from "@core/domain/types/either";
import { inject, injectable } from "inversify";
import { DateTime } from "luxon";
import type {
    KanbanPendingByAssigneeBucketDto,
    KanbanPendingByTeamBucketDto,
    KanbanReportActivityItemDto,
    KanbanStatusSummaryDto,
} from "../dto/kanban-reports.dto";

const PROJECTS_PATH = "/projects/";

@injectable()
export class ProjectKanbanReportsDataSource {
    constructor(
        @inject(coreTypes.infrastructure.Http)
        private readonly http: Http,
    ) {}

    async fetchStatusSummary(
        projectId: number,
    ): Promise<Either<FallbackError, KanbanStatusSummaryDto>> {
        const responseResult = await this.http.get<KanbanStatusSummaryDto>(
            `${PROJECTS_PATH}${projectId}/kanban-status-summary/`,
        );

        return responseResult
            .mapLeft(() => new FallbackError())
            .map((response) => response.data);
    }

    async fetchPending(
        projectId: number,
    ): Promise<Either<FallbackError, KanbanReportActivityItemDto[]>> {
        const responseResult = await this.http.get<
            KanbanReportActivityItemDto[]
        >(`${PROJECTS_PATH}${projectId}/kanban-pending/`);

        return responseResult
            .mapLeft(() => new FallbackError())
            .map((response) => response.data);
    }

    async fetchPendingByAssignee(
        projectId: number,
    ): Promise<Either<FallbackError, KanbanPendingByAssigneeBucketDto[]>> {
        const responseResult = await this.http.get<
            KanbanPendingByAssigneeBucketDto[]
        >(`${PROJECTS_PATH}${projectId}/kanban-pending-by-assignee/`);

        return responseResult
            .mapLeft(() => new FallbackError())
            .map((response) => response.data);
    }

    async fetchPendingByTeam(
        projectId: number,
    ): Promise<Either<FallbackError, KanbanPendingByTeamBucketDto[]>> {
        const responseResult = await this.http.get<
            KanbanPendingByTeamBucketDto[]
        >(`${PROJECTS_PATH}${projectId}/kanban-pending-by-team/`);

        return responseResult
            .mapLeft(() => new FallbackError())
            .map((response) => response.data);
    }

    static parseDeadline(
        deadlineIso: string | null | undefined,
    ): DateTime | null {
        if (!deadlineIso) return null;

        const parsed = DateTime.fromISO(deadlineIso);
        return parsed.isValid ? parsed : null;
    }
}
